"use client"

import { useState } from "react"
import { sameOriginBlobUrl } from "@/lib/blob-proxy"
import { cn } from "@/lib/utils"
import { useInsuranceTranslation } from "@/lib/insurance/use-insurance-translation"
import { getServices, formatCHF } from "@/lib/insurance/insurers"
import { getServiceIcon } from "@/lib/insurance/insurer-assets"
import type { Service } from "@/lib/insurance/insurance-types"

interface ServiceDropdownProps {
  /** The check-up being priced: a `*-mri` or `blood-test` id, or "". */
  serviceId: string
  /** The membership tier selected alongside it. Held separately from
   *  `serviceId` because the two coexist: every check-up is the Ahead
   *  check-up at 1x, 2x or 4x a year, an MRI on top of it at the member rate.
   *  The form always has one (1x to start); "" only renders list prices. */
  membershipId: string
  onServiceChange: (id: string) => void
  onMembershipChange: (id: string) => void
}

function isMembershipId(id: string): boolean {
  return id.startsWith("membership-")
}

function CheckIcon() {
  return (
    <svg
      className="service-check"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      width="18"
      height="18"
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  )
}

export function ServiceDropdown({
  serviceId,
  membershipId,
  onServiceChange,
  onMembershipChange,
}: ServiceDropdownProps) {
  const { t } = useInsuranceTranslation()
  const services = getServices()

  // The membership row leads, labelled as the advanced blood test: the
  // one-off blood test is no longer offered as its own row (Nadjib,
  // 2026-10-15), so the blood test is bought through a tier. The MRIs follow
  // from the most complete (and priciest) down: Pro, Advanced, Core. The
  // catalogue keeps `blood-test` and its own order (the pro forma and the
  // results read it), so the step's rows and order are set here only.
  const otherCheckups = services
    .filter((svc) => !isMembershipId(svc.id) && svc.id !== "blood-test")
    .sort((a, b) => b.price - a.price)
  const membershipTiers = services.filter((svc) => isMembershipId(svc.id))
  const membershipFrom = Math.min(...membershipTiers.map((svc) => svc.price))

  const [membershipExpanded, setMembershipExpanded] =
    useState<boolean>(!!membershipId)
  const membershipIcon = getServiceIcon("membership-group")
  const membershipSelected = !!membershipId
  // Member pricing follows the TIER selection, not the row being open: merely
  // opening the row is browsing, and re-pricing everything under a chevron
  // click reads as the prices moving on their own. Picking 1x / 2x / 4x is the
  // commitment that earns the member rate, and it SURVIVES picking a check-up
  // below — that is the whole point of the two living in separate state.
  const memberPricing = membershipSelected

  function handleRegularSelect(id: string) {
    // Collapse the tiers only when there is nothing selected in them to hide.
    // With a membership picked the row has to stay open: it is showing the
    // tier this check-up is being priced against.
    if (!membershipSelected) setMembershipExpanded(false)
    // Clicking the chosen check-up again clears it, the same way a tier does.
    // Without it the pair is a one-way door: a visitor on "2x per year + Core
    // MRI" who decides they only want the membership has no way back.
    onServiceChange(id === serviceId ? "" : id)
  }

  function handleMembershipParentClick() {
    setMembershipExpanded((prev) => !prev)
  }

  function handleTierSelect(id: string) {
    // A tier is switched, never cleared: the Ahead check-up is part of every
    // booking, so clicking the chosen tier again keeps it.
    if (id !== membershipId) onMembershipChange(id)
  }

  function renderService(svc: Service) {
    const icon = getServiceIcon(svc.id)
    return (
      <div
        key={svc.id}
        className={cn("service-option", svc.id === serviceId && "selected")}
        role="option"
        aria-selected={svc.id === serviceId}
        tabIndex={0}
        onClick={() => handleRegularSelect(svc.id)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault()
            handleRegularSelect(svc.id)
          }
        }}
      >
        {icon && (
          <div
            className={cn(
              "service-icon",
              icon.border && "icon-border",
              icon.photo && "icon-photo"
            )}
            style={{ background: icon.bg }}
          >
            <img
              src={sameOriginBlobUrl(icon.icon)}
              alt=""
              width={icon.photo ? 36 : 24}
              height={icon.photo ? 36 : 24}
            />
          </div>
        )}
        <div className="service-info">
          <div className="service-row">
            <div className="service-name">
              {t(`form.service.name.${svc.id}`)}
            </div>
            <div className="service-price">
              {memberPricing && svc.memberPrice != null ? (
                <>
                  <span className="service-price-regular">
                    CHF {formatCHF(svc.price)}
                  </span>{" "}
                  <span className="service-price-member">
                    CHF {formatCHF(svc.memberPrice)}
                  </span>
                  <span className="service-price-note">
                    {t("form.service.member_price")}
                  </span>
                </>
              ) : (
                <>CHF {formatCHF(svc.price)}</>
              )}
            </div>
          </div>
          <div className="service-desc">{t(`form.service.desc.${svc.id}`)}</div>
        </div>
        {svc.id === serviceId && <CheckIcon />}
      </div>
    )
  }

  return (
    <div>
      <div className="service-options">
        {membershipTiers.length > 0 && (
          <>
            <div
              className={cn("service-option", membershipSelected && "selected")}
              role="button"
              aria-expanded={membershipExpanded}
              tabIndex={0}
              onClick={handleMembershipParentClick}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault()
                  handleMembershipParentClick()
                }
              }}
            >
              {membershipIcon && (
                <div
                  className={cn(
                    "service-icon",
                    membershipIcon.border && "icon-border",
                    membershipIcon.photo && "icon-photo"
                  )}
                  style={{ background: membershipIcon.bg }}
                >
                  <img
                    src={sameOriginBlobUrl(membershipIcon.icon)}
                    alt=""
                    width={membershipIcon.photo ? 36 : 24}
                    height={membershipIcon.photo ? 36 : 24}
                  />
                </div>
              )}
              <div className="service-info">
                <div className="service-row">
                  <div className="service-name">
                    {t("form.service.membership.label")}
                  </div>
                  <div className="service-price">
                    {t("form.service.membership.from", {
                      amount: formatCHF(membershipFrom),
                    })}
                  </div>
                </div>
                <div className="service-desc">
                  {t("form.service.membership.desc")}
                </div>
              </div>
              <svg
                className={cn("service-chevron", membershipExpanded && "open")}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                width="18"
                height="18"
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </div>

            {membershipExpanded && (
              <div
                className="service-tiers"
                role="group"
                aria-label={t("form.service.membership.label")}
              >
                {membershipTiers.map((tier) => (
                  <div
                    key={tier.id}
                    className={cn(
                      "service-option",
                      "service-tier",
                      tier.id === membershipId && "selected"
                    )}
                    role="option"
                    aria-selected={tier.id === membershipId}
                    tabIndex={0}
                    onClick={() => handleTierSelect(tier.id)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault()
                        handleTierSelect(tier.id)
                      }
                    }}
                  >
                    <div className="service-info">
                      <div className="service-row">
                        <div className="service-name">
                          {t(`form.service.name.${tier.id}`)}
                        </div>
                        <div className="service-price">
                          CHF {formatCHF(tier.price)}
                        </div>
                      </div>
                    </div>
                    {tier.id === membershipId && <CheckIcon />}
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {otherCheckups.map(renderService)}
      </div>
    </div>
  )
}
